import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/utils-ros";
import { FileStack, Plus, Trash2, Send } from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { UploadRepositoryDocumentModal } from "@/components/revenueos/UploadRepositoryDocumentModal";
import { RequestDeletionModal } from "@/components/revenueos/RequestDeletionModal";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const EXT_LABEL = { ".pdf": "PDF", ".pptx": "PPTX", ".docx": "DOCX" };

/**
 * RepositoryDocumentsPage — shared list view for the 3 Repositories subpages
 * (Customer Presentations / Technical Proposals / Commercial Proposals).
 */
export function RepositoryDocumentsPage({ category, title, description }) {
  const { user, isSuperAdmin, isAdmin, isFounder, isPartner, canWrite } = useAuth();
  const canUpload = canWrite("repositories");
  const canDeleteDirect = isSuperAdmin || isAdmin || isFounder;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [requestDeleteFor, setRequestDeleteFor] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/repositories", { params: { category } });
      setDocs(r.data);
    } catch {
      toast.error("Could not load documents");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openFile(doc) {
    try {
      const res = await api.get(`/repositories/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open file");
    }
  }

  async function deleteDirect(doc) {
    if (!window.confirm(`Delete "${doc.customer_name}"? This can't be undone.`)) return;
    setBusy(doc.id);
    try {
      await api.delete(`/repositories/${doc.id}`);
      toast.success("Document deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  }

  const shown = docs.filter(d => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [d.customer_name, d.created_by, d.created_for, d.file_name].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="repository-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">{title}</h1>
            <p className="text-[13px] text-white/75 mt-1">{description}</p>
          </div>
          {canUpload && (
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="repo-add-btn"
              className="h-10 px-4 rounded-xl bg-[#E58D22] hover:bg-[#C97715] !text-white text-[12px] font-semibold flex items-center gap-2 shrink-0 shadow-[0_7px_18px_rgba(229,141,34,0.28)]"
            >
              <Plus size={14} /> Add
            </button>
          )}
        </div>
      </section>

      <UploadRepositoryDocumentModal open={uploadOpen} onOpenChange={setUploadOpen} category={category} onCreated={() => load()} />
      <RequestDeletionModal
        open={Boolean(requestDeleteFor)}
        onOpenChange={(open) => !open && setRequestDeleteFor(null)}
        entityType="repository_document"
        entityId={requestDeleteFor?.id}
        entityLabel={requestDeleteFor?.customer_name}
        onSubmitted={() => setRequestDeleteFor(null)}
      />

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search by customer, created by, or created for…"
          entityLabel="documents"
          count={shown.length}
          variant="universe"
          sparPrompts={[]}
        />

        {loading ? <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_10px_28px_rgba(7,83,154,0.08)]">Loading documents…</div> :
         shown.length === 0 ? (
           <div className="text-center p-10 border border-dashed border-[#B8D3DF] bg-white rounded-[22px]">
             <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><FileStack size={21} /></span>
             <div className="text-[13px] text-[#78909C]">No documents uploaded yet.</div>
           </div>
         ) : (
          <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="repo-table-scroll">
            <div className="grid min-w-[880px] grid-cols-[minmax(200px,1.6fr)_110px_160px_200px_130px] gap-3 px-4 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[10px] font-semibold text-[#07539A]">
              <div>Customer name</div>
              <div>Date presented</div>
              <div>Created by</div>
              <div>Created for</div>
              <div className="text-center">Actions</div>
            </div>
            <div className="min-w-[880px] divide-y divide-[#E1EBF0]">
              {shown.map(d => {
                const canRequestDelete = isPartner && d.primary_owner_id === user?.id;
                return (
                  <div
                    key={d.id}
                    data-testid={`repo-row-${d.id}`}
                    onClick={() => openFile(d)}
                    className="grid grid-cols-[minmax(200px,1.6fr)_110px_160px_200px_130px] gap-3 items-center px-4 py-4 hover:bg-[#F8FCFE] transition-colors cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium text-[#455A64] truncate">{d.customer_name}</div>
                      <div className="text-[10px] text-[#78909C] truncate mt-0.5">{d.file_name} · {EXT_LABEL[d.file_ext] || d.file_ext}</div>
                    </div>
                    <div className="text-[11px] text-[#607D8B]">{formatDate(d.date_presented)}</div>
                    <div className="text-[11px] text-[#607D8B] truncate">{d.created_by || "—"}</div>
                    <div className="text-[11px] text-[#607D8B] truncate">{d.created_for || "—"}</div>
                    <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                      {canDeleteDirect && (
                        <button
                          onClick={() => deleteDirect(d)}
                          disabled={busy === d.id}
                          data-testid={`repo-delete-${d.id}`}
                          className="h-8 px-2.5 rounded-lg border border-[#E49B96] bg-white hover:bg-[#FFF1F0] text-[#B42318] text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      )}
                      {!canDeleteDirect && canRequestDelete && (
                        <button
                          onClick={() => setRequestDeleteFor(d)}
                          data-testid={`repo-request-delete-${d.id}`}
                          className="h-8 px-2.5 rounded-lg border border-[#B8D3DF] bg-white hover:border-[#07539A] hover:text-[#07539A] text-[#607D8B] text-[10px] font-medium whitespace-nowrap flex items-center gap-1"
                        >
                          <Send size={11} /> Request deletion
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
